import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useT } from "../contexts/LanguageContext";
import LanguageSwitcher from "../components/app/LanguageSwitcher";
import { toast } from "sonner";
import { GoogleLogo } from "@phosphor-icons/react";

export default function Login() {
  const { t } = useT();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("demo@pmcoach-ai.com");
  const [password, setPassword] = useState("Demo12345!");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      const { trackEvent } = await import("../components/app/AnalyticsPixels");
      trackEvent("login", { method: "email" });
      const to = location.state?.from?.pathname || "/dashboard";
      navigate(to, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.login_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 sm:p-16">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
              <img src="/logo-full.png" alt="PMCoach AI" className="h-12 w-auto" data-testid="brand-logo" />
            </Link>
            <LanguageSwitcher />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-10">{t("auth.welcome_back")}</h1>
          <p className="text-slate-600 mt-2">{t("auth.welcome_back_sub")}</p>

          <button onClick={onGoogle} className="btn-secondary w-full mt-6 flex items-center justify-center gap-2" data-testid="google-login-button">
            <GoogleLogo size={18} weight="bold" /> {t("common.cont_with_google")}
          </button>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-slate-400">
            <div className="flex-1 h-px bg-slate-200" /> {t("common.or")} <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={onSubmit} className="space-y-3" data-testid="login-form">
            <label className="overline">{t("common.email")}</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
            <label className="overline">{t("common.password")}</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
            <button type="submit" disabled={submitting} className="btn-primary w-full mt-3" data-testid="login-submit-button">
              {submitting ? t("auth.signing_in") : t("common.signin")}
            </button>
          </form>

          <div className="mt-5 text-sm text-slate-600 flex items-center justify-between">
            <span>
              {t("auth.no_account")}{" "}
              <Link to="/register" className="text-[#002FA7] font-semibold hover:underline" data-testid="goto-register">{t("auth.create_account")}</Link>
            </span>
            <Link to="/forgot-password" className="text-slate-500 hover:text-[#002FA7] hover:underline text-xs" data-testid="goto-forgot">
              {t("auth.forgot_password") || "Parolamı unuttum"}
            </Link>
          </div>
          <div className="mt-3 ai-callout text-sm">
            <div className="overline">{t("common.demo_account").toUpperCase()}</div>
            demo@pmcoach-ai.com / Demo12345!
          </div>
        </div>
      </div>
      <div className="hidden lg:block relative bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1739301674047-386ed15c7e16?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Mock exam results"
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="relative z-10 h-full p-12 flex flex-col justify-end text-white">
          <div className="overline text-blue-300">{t("auth.testimonial_attribution").includes("PMP") ? "TESTIMONIAL" : "REFERANS"}</div>
          <p className="font-display text-2xl mt-3 leading-snug max-w-md">{t("auth.testimonial")}</p>
          <p className="text-slate-200 mt-2 text-sm">{t("auth.testimonial_attribution")}</p>
        </div>
      </div>
    </div>
  );
}
